import {
  AfterViewInit,
  computed,
  ContentChildren,
  Directive, effect,
  forwardRef,
  HostBinding,
  HostListener,
  Input, OnDestroy,
  QueryList,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { mergeWith, noop, Subscription } from 'rxjs';
import { Keys } from '../../../../_core/utils/keys';
import { RadioElementDirective } from './radio-element.directive';
import { RadioComponent } from './radio.component';

@Directive({
  selector: '[opac-radio-group]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioGroupDirective),
      multi: true,
    },
  ],
})
export class RadioGroupDirective implements AfterViewInit, OnDestroy, ControlValueAccessor {
  private readonly radioButtons = signal<RadioElementDirective[]>([]);

  private readonly focusedRadioButton = signal<RadioElementDirective | undefined>(undefined);
  private readonly selectedRadioButton = signal<RadioElementDirective | undefined>(undefined);
  private readonly focusedRadioButtonIndex = computed(() => this.radioButtons().findIndex((radio) => radio.id === this.focusedRadioButton()?.id) ?? 0);

  private radioButtonsChangeSubscriptions: Subscription[] = [];
  private isFirstTimeProcessingRadioButtons = true;
  private initialValue: string | undefined = undefined;

  @HostBinding('attr.tabindex')
  get hostTabindex() {
    return 0;
  }

  @HostBinding('attr.role')
  get hostRole() {
    return 'radiogroup';
  }

  @HostListener('keydown', ['$event'])
  onHostKeydown(event: KeyboardEvent) {
    if (event.key === Keys.ArrowRight || event.key === Keys.ArrowDown) {
      const currentIndex = this.focusedRadioButtonIndex();
      let nextIndex = currentIndex + 1;

      if (nextIndex >= this.radioButtons().length) {
        nextIndex = 0;
      }

      this.selectRadioButton(this.radioButtons()[nextIndex]);
      event.preventDefault();
    }

    if (event.key === Keys.ArrowLeft || event.key === Keys.ArrowUp) {
      const currentIndex = this.focusedRadioButtonIndex();
      let previousIndex = currentIndex - 1;

      if (previousIndex < 0) {
        previousIndex = this.radioButtons().length - 1;
      }

      this.selectRadioButton(this.radioButtons()[previousIndex]);
      event.preventDefault();
    }

    if (event.key === Keys.Space) {
      this.selectRadioButton(this.focusedRadioButton());
      event.preventDefault();
    }
  }

  @HostListener('focus')
  onHostFocus() {
    if (this.radioButtons().length === 0) {
      return;
    }

    this.focusedRadioButton.set(this.selectedRadioButton());

    if (this.selectedRadioButton() === undefined) {
      this.focusedRadioButton.set(this.radioButtons()[0]);
    }

    this.focusedRadioButton().focus();
    this.onTouched();
  }

  /**
   * Select all custom Radio elements
   */
  @ContentChildren(RadioElementDirective) radioElementDirectives!: QueryList<RadioElementDirective>;
  /**
   * Select radio components
   */
  @ContentChildren(RadioComponent) radioComponents!: QueryList<RadioComponent>;

  /**
   * The name of the radio group. This name should coincide with the name of the radio buttons.
   */
  @Input('opac-radio-group') name: string;

  readonly value = signal<string>('');

  onChange: (value: string) => void = noop;
  onTouched: () => void = noop;

  constructor() {
    effect(() => {
      const radioComponent = this.selectedRadioButton();

      if (!radioComponent) {
        return;
      }

      radioComponent.checked = true;

      this.onChange(radioComponent.value);
    }, { allowSignalWrites: true });

    effect(() => {
      const radioComponent = this.focusedRadioButton();
      radioComponent?.focus();
    });
  }

  ngAfterViewInit() {
    // Combine both radios and process them first time
    const radios = [...this.radioElementDirectives, ...this.radioComponents.map(c => c.directive)];
    this.processRadioButtons([...radios]).then((processedRadios) => this.radioButtons.set(processedRadios));

    // Everytime any QueryList changes update all radios
    this.radioElementDirectives.changes
      .pipe(mergeWith([this.radioComponents.changes]))
      .subscribe(() => {
        const r = [...this.radioElementDirectives, ...this.radioComponents.map(c => c.directive)];
        this.processRadioButtons([...r]).then((processedRadios) => this.radioButtons.set(processedRadios));
      });
  }

  ngOnDestroy() {
    this.radioButtonsChangeSubscriptions.forEach((s) => s.unsubscribe());
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value: string): void {
    this.initialValue = value;
    this.value.set(value);
  }

  private async processRadioButtons(components: RadioElementDirective[]): Promise<RadioElementDirective[]> {
    this.radioButtonsChangeSubscriptions.forEach((s) => {
      s.unsubscribe();
    });
    this.radioButtonsChangeSubscriptions = [];

    // Wait a tick to get `this.initialValue` work
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

    const radioButtons = components.filter((radio) => radio.name === this.name);

    radioButtons.forEach((radioButton) => {
      // Check if this is the first time that options are processed.
      // This only occurs when component is initialized first time, if
      // any option is added/removed via *ngIf or *ngFor this code won't run.
      if (this.isFirstTimeProcessingRadioButtons) {
        // Check if there is any initial value assigned by the FormControl
        if (this.initialValue && radioButton.value === this.initialValue) {
          this.selectRadioButton(radioButton, false);
        }

        // If there is no initial value set, check if any radio is checked
        if (!this.initialValue && radioButton.checked) {
          this.selectRadioButton(radioButton, false);
        }
      }

      this.radioButtonsChangeSubscriptions.push(
        radioButton.change.subscribe((checked) => {
          if (checked) {
            this.selectRadioButton(radioButton);
          }
        }),
      );
    });

    this.isFirstTimeProcessingRadioButtons = false;
    return radioButtons;
  }

  private selectRadioButton(radioComponent: RadioElementDirective, focus = true): void {
    if (this.selectedRadioButton()) {
      this.selectedRadioButton().checked = false;
    }

    this.selectedRadioButton.set(radioComponent);

    if (focus) {
      this.focusedRadioButton.set(radioComponent);
    }
  }
}
